import { formatDateOnly } from '../../../common/utils/date';
import { PREG_STATE } from '../../../core/constants/dx';
import { embryoStageColor, embryoStageLabel, isFrozen, storageLabel } from '../../../core/constants/transfers';
import { useSessionClivages } from '../../clivages/contexts/clivages';

// Resumen (solo lectura) de las fases posteriores de una OPU, para la vista de sesión:
// Clivajes → Transferencias + Dx → Partos. Los datos ya vienen del contrato
// (clivages por aspiración; transfer_sessions con pregnancy.state por transfer).

function EmbryoCell({ embryo }) {
    if (!embryo?.code) return <span className="opu-land__muted-inline">—</span>;
    return (
        <span className="opu-land__emb">
            {embryo.code}
            {embryo.stage && <span className="opu-land__stage" style={{ '--stage': embryoStageColor(embryo.stage) }}>{embryoStageLabel(embryo.stage)}</span>}
            {isFrozen(embryo.storage) && <span className="opu-land__frozen">{storageLabel(embryo.storage)}</span>}
        </span>
    );
}

// ---- Clivajes ----
export function ClivagesPanel({ sessionId, aspirationIds }) {
    const { data, isLoading } = useSessionClivages(sessionId, aspirationIds);
    const clivages = data || [];

    return (
        <section className="opu-land__table-card">
            <h2 className="opu-land__section-title">Clivajes{clivages.length > 0 && <span className="opu-land__section-count">{clivages.length}</span>}</h2>
            {isLoading ? (
                <p className="opu-land__muted">Cargando clivajes…</p>
            ) : !clivages.length ? (
                <p className="opu-land__muted">Aún sin clivajes registrados.</p>
            ) : (
                <table className="opu-land__table">
                    <thead>
                        <tr><th>#</th><th>Donante</th><th>Toro</th><th>Lab</th><th>Óvocitos</th><th>Embriones</th><th>Etapas</th></tr>
                    </thead>
                    <tbody>
                        {clivages.map((c, i) => {
                            const stages = c.stages || {};
                            return (
                                <tr key={c.id}>
                                    <td>{i + 1}</td>
                                    <td className="opu-land__td-name">{c.aspiration?.donor?.name || '—'}</td>
                                    <td className="opu-land__td-name">{c.semen?.sire?.name || c.semen?.code || '—'}</td>
                                    <td>{c.lab?.name || '—'}</td>
                                    <td>{c.oocytes ?? '—'}</td>
                                    <td>{c.embryos_count ?? 0}</td>
                                    <td className="opu-land__td-stages">
                                        {Object.keys(stages).length ? Object.entries(stages).map(([k, n]) => (
                                            <span key={k} className="opu-land__stage" style={{ '--stage': embryoStageColor(k) }}>{embryoStageLabel(k)} · {n}</span>
                                        )) : <span className="opu-land__muted-inline">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </section>
    );
}

// ---- Transferencias + Dx (agrupadas por sesión T.E.) ----
export function TransfersDxPanel({ teSessions = [] }) {
    const total = teSessions.reduce((a, s) => a + (s.transfers?.length || 0), 0);

    return (
        <section className="opu-land__table-card">
            <h2 className="opu-land__section-title">Transferencias + Dx{total > 0 && <span className="opu-land__section-count">{total}</span>}</h2>
            {!teSessions.length ? (
                <p className="opu-land__muted">Aún sin transferencias.</p>
            ) : (
                <div className="opu-land__groups">
                    {teSessions.map((s) => (
                        <div key={s.id} className="opu-land__te">
                            <div className="opu-land__te-head">
                                <span className="opu-land__te-farm">{s.farm?.name || 'Sin finca'}</span>
                                <span className="opu-land__te-code">{s.code || '—'}</span>
                                {s.date && <span className="opu-land__te-date">{formatDateOnly(s.date)}</span>}
                            </div>
                            <table className="opu-land__table">
                                <thead>
                                    <tr><th>Receptora</th><th>Embrión</th><th>Estado (Dx)</th></tr>
                                </thead>
                                <tbody>
                                    {(s.transfers || []).map((t) => {
                                        const st = PREG_STATE[t.pregnancy?.state];
                                        return (
                                            <tr key={t.id}>
                                                <td className="opu-land__td-name">{t.recipient?.number || '—'}{t.recipient?.name ? ` · ${t.recipient.name}` : ''}</td>
                                                <td><EmbryoCell embryo={t.embryo} /></td>
                                                <td>{st ? <span className={`opu-land__preg opu-land__preg--${st.tone}`}>{st.label}</span> : <span className="opu-land__muted-inline">Sin dx</span>}</td>
                                            </tr>
                                        );
                                    })}
                                    {!(s.transfers || []).length && <tr><td colSpan={3} className="opu-land__muted-inline">Sin transferencias en esta sesión.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

// ---- Partos (solo si ya hay preñeces `born`) ----
export function PartosPanel({ teSessions = [] }) {
    const rows = [];
    teSessions.forEach((s) => {
        (s.transfers || []).forEach((t) => {
            if (t.pregnancy?.state === 'born') rows.push({ id: t.id, recipient: t.recipient, embryo: t.embryo, farm: s.farm });
        });
    });
    if (!rows.length) return null;

    return (
        <section className="opu-land__table-card">
            <h2 className="opu-land__section-title">Partos<span className="opu-land__section-count">{rows.length}</span></h2>
            <table className="opu-land__table">
                <thead>
                    <tr><th>Receptora</th><th>Embrión</th><th>Finca</th><th>Estado</th></tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id}>
                            <td className="opu-land__td-name">{r.recipient?.number || '—'}{r.recipient?.name ? ` · ${r.recipient.name}` : ''}</td>
                            <td><EmbryoCell embryo={r.embryo} /></td>
                            <td>{r.farm?.name || '—'}</td>
                            <td><span className="opu-land__preg opu-land__preg--good">Nacida</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}
